import { localize } from "@/i18n/translate";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useContent } from "@/i18n/LangContext";
import { scrollToTop } from "@/lib/scroll";
import { Card } from "@/components/site/Publications";
import { SectionLink } from "@/components/site/SectionLink";
import { translateTree } from "@/i18n/translate";
import ARTICLES from "@/data/articles.json";
import TRANSLATIONS from "@/data/articles.translations.json";
const toBlocks = items => items.reduce((acc, [tag, text]) => {
  const last = acc[acc.length - 1];
  if (tag === "li") {
    if (last?.tag === "ul") last.items.push(text);else acc.push({
      tag: "ul",
      items: [text]
    });
  } else acc.push({
    tag,
    text
  });
  return acc;
}, []);
const Block = ({
  b
}) => {
  if (b.tag === "ul") return <ul className="space-y-3">
        {localize(b.items.map(t => <li key={t} className="flex gap-4"><span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 bg-signal" aria-hidden="true" />{localize(t)}</li>))}
      </ul>;
  if (b.tag === "h2") return <h2 className="!mt-14 font-display text-2xl font-bold leading-tight tracking-[-0.02em] text-ink md:text-3xl">{localize(b.text)}</h2>;
  if (b.tag === "h3") return <h3 className="!mt-10 font-display text-xl font-bold tracking-[-0.01em] text-ink">{localize(b.text)}</h3>;
  return <p>{localize(b.text)}</p>;
};
export default function Article() {
  const {
    slug
  } = useParams();
  const {
    PUBLICATIONS,
    ui,
    lang
  } = useContent();
  const t = ui.pubs;
  const pub = PUBLICATIONS.find(p => p.slug === slug);
  const baseItems = lang === "de" ? pub?.code === "de" ? ARTICLES[slug] : TRANSLATIONS[slug] || ARTICLES[slug] : pub?.code === "en" ? ARTICLES[slug] : TRANSLATIONS[slug] || ARTICLES[slug];
  const items = translateTree(baseItems, lang);
  useEffect(() => {
    scrollToTop();
  }, [slug]);
  useEffect(() => {
    if (!pub) return undefined;
    document.title = `${pub.title} – Swiss Blockchain Alliance`;
    return () => {
      document.title = ui.title;
    };
  }, [pub, ui.title]);
  if (!pub || !Array.isArray(items)) {
    return <div className="mx-auto max-w-[1440px] px-5 pb-32 pt-[160px] sm:px-8 lg:px-12" data-testid="article-not-found">
        <h1 className="font-display text-4xl font-extrabold tracking-[-0.03em]">{localize(t.notFound)}</h1>
        <Link to="/#publikationen" className="mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 font-medium hover:text-signal">{localize(t.back)}</Link>
      </div>;
  }
  const others = PUBLICATIONS.filter(p => p.slug !== slug);
  return <article data-testid="article-page" className="pb-24 pt-[112px] md:pb-32 md:pt-[140px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <SectionLink to="publikationen" className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-signal" data-testid="article-back-link">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {localize(t.back)}
        </SectionLink>
        <header className="mt-10 grid grid-cols-12 gap-x-6 gap-y-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow"><span className="text-signal">{localize(pub.date)}</span> · {localize(pub.lang)}</p>
            <h1 lang={lang} className="mt-6 font-display text-[2rem] font-extrabold leading-[1.02] tracking-[-0.035em] [hyphens:auto] sm:text-5xl lg:text-6xl" data-testid="article-title">{localize(pub.title)}</h1>
          </div>
        </header>
        <div className="relative mt-12 aspect-[16/9] overflow-hidden bg-ink [clip-path:polygon(0_0,100%_0,100%_100%,6%_100%,0_88%)] md:mt-16 md:aspect-[21/9]">
          <img src={pub.img} alt="" className="h-full w-full object-cover grayscale" />
          <span className="absolute left-0 top-0 h-12 w-12 bg-signal" aria-hidden="true" />
        </div>
        <div className="mt-14 grid grid-cols-12 gap-x-6 md:mt-20">
          <div className="col-span-12 lg:col-span-7 lg:col-start-4">
            <div lang={TRANSLATIONS[slug] ? lang : pub.code} className="space-y-6 text-base leading-[1.75] text-ink/85 md:text-lg" data-testid="article-body">
              {localize(toBlocks(items).map((b, i) => <Block key={i} b={b} />))}
            </div>
            <div className="mt-16 flex flex-col gap-5 border-t border-ink/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-sm font-display text-xl font-bold tracking-[-0.01em]">{localize(t.cta)}</p>
              <SectionLink to="kontakt" className="inline-flex items-center justify-center gap-3 bg-signal px-6 py-4 font-medium text-white transition-colors hover:bg-signaldark" data-testid="article-cta">
                {localize(t.ctaBtn)} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </SectionLink>
            </div>
          </div>
        </div>
        <section className="mt-24 md:mt-32" aria-labelledby="more-h">
          <h2 id="more-h" className="eyebrow">{localize(t.more)}</h2>
          <div className="mt-8 grid gap-x-6 gap-y-14 md:grid-cols-2">
            {localize(others.map((p, i) => <Card key={p.slug} p={p} i={i} t={t} />))}
          </div>
        </section>
      </div>
    </article>;
}
